import { carcCodes, rarcCodes } from '~/entities/carc-rarc'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { centsToDollars, formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault } from '~/shared/lib/datetime'
import type {
  AdjustmentDiffData,
  AuditTrailEventDescriptionField,
  CheckedPatientDiffData,
  EobProcessingEventProps,
  EobProcessingFinishedEventProps,
  PatientClaimDiffData,
  ServiceLineDiffData,
} from '../../model'
import type { ArrayDiffFieldConfig } from '../compute-array-diff'
import { computeArrayDiff, computeArrayInitial } from '../compute-array-diff'
import type { EobAuditEvent } from '../eob-types'
import { asEobEvent, findPreviousPaymentSummaryEvent } from '../eob-types'
import { collectFieldSearchText } from './searchable-text-utils'

// ---------------------------------------------------------------------------
// Payment summary config (unchanged)
// ---------------------------------------------------------------------------

/** Payment type enum -> display label */
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  check: 'Check',
  direct_deposit: 'Direct Deposit',
  credit_card: 'Credit Card',
}

interface PaymentFieldConfig {
  key: keyof apiClient.PaymentSummaryResponse
  label: string
  format: (value: unknown) => string | null
}

const PAYMENT_SUMMARY_FIELDS: PaymentFieldConfig[] = [
  {
    key: 'payment_type',
    label: 'Payment Type',
    format: (v) => (typeof v === 'string' ? (PAYMENT_TYPE_LABELS[v] ?? v) : null),
  },
  {
    key: 'total_paid',
    label: 'Total Paid',
    format: (v) => {
      const dollars = centsToDollars(v as number | string | null | undefined)
      return dollars !== null ? formatCurrency(dollars) : null
    },
  },
  {
    key: 'check_date',
    label: 'Check Date',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  {
    key: 'deposit_date',
    label: 'Deposit Date',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  {
    key: 'check_number',
    label: 'Check Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'ach_trace_number',
    label: 'ACH Trace Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'bank_reference_number',
    label: 'Bank Reference Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'tax_id',
    label: 'Tax ID',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'customer_service_number',
    label: 'Customer Service Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'number_of_patient_claims',
    label: 'Number of Patient Claims',
    format: (v) => (typeof v === 'number' ? String(v) : null),
  },
]

// ---------------------------------------------------------------------------
// Shared format helpers
// ---------------------------------------------------------------------------

const formatString = (v: unknown): string | null => (typeof v === 'string' ? v : null)

const formatCents = (v: unknown): string | null => {
  const dollars = centsToDollars(v as number | string | null | undefined)
  return dollars !== null ? formatCurrency(dollars) : null
}

// ---------------------------------------------------------------------------
// Adjustment config
// ---------------------------------------------------------------------------

const ADJUSTMENT_TYPE_LABELS: Record<string, string> = {
  FB: 'Forward balance',
  WO: 'Overpayment recovery',
  CS: 'Adjustment',
  '72': 'Authorized return',
  L6: 'Interest owed',
  IR: 'Internal revenue service withholding',
  U1: 'Non-reimbursable',
  E3: 'Affiliated withholding',
  C5: 'Manual invoices',
}

const formatAdjustmentType = (v: unknown): string | null => {
  if (typeof v !== 'string' || !v) return null
  const label = ADJUSTMENT_TYPE_LABELS[v]
  return label ? `${v} - ${label}` : v
}

const ADJUSTMENT_FIELD_CONFIGS: ArrayDiffFieldConfig[] = [
  { key: 'code', label: 'Adjustment Type', format: formatAdjustmentType },
  { key: 'amount', label: 'Amount', format: formatCents },
  { key: 'tracking_number', label: 'Tracking Number', format: formatString },
  { key: 'notes', label: 'Notes', format: formatString },
  {
    key: 'deduct_from_total_paid',
    label: 'Deducted From Total Paid',
    format: (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : null),
  },
]

// ---------------------------------------------------------------------------
// Checked patients config
// ---------------------------------------------------------------------------

const CHECKED_PATIENT_FIELD_CONFIGS: ArrayDiffFieldConfig[] = [
  { key: 'patient_account_number', label: 'Patient Account Number', format: formatString },
  { key: 'patient_first_name', label: 'Patient First Name', format: formatString },
  { key: 'patient_last_name', label: 'Patient Last Name', format: formatString },
]

// ---------------------------------------------------------------------------
// Patient claims config
// ---------------------------------------------------------------------------

const formatAmount = (v: unknown): string | null => {
  if (v == null || typeof v !== 'object') return null
  const { amount, amountType } = v as { amount?: string | null; amountType?: string | null }
  if (amount == null) return null
  if (amountType === 'percentage') return `${Math.round(Number(amount) * 10000) / 100}%`
  const dollars = centsToDollars(amount)
  return dollars !== null ? formatCurrency(dollars) : null
}

const CLAIM_FIELD_CONFIGS: ArrayDiffFieldConfig[] = [
  { key: 'patient_account_number', label: 'Patient Account #', format: formatString },
  { key: 'total_paid', label: 'Total Paid', format: formatCents },
  { key: 'patient_first_name', label: 'Patient First Name', format: formatString },
  { key: 'patient_last_name', label: 'Patient Last Name', format: formatString },
  {
    key: 'patient_dob',
    label: 'Date of Birth',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  { key: 'claim_number', label: 'Claim Number', format: formatString },
  { key: 'member_id', label: 'Member ID', format: formatString },
  { key: 'service_provider_name', label: 'Provider', format: formatString },
  {
    key: 'page_of_doc',
    label: 'Page of Doc',
    format: (v) => (typeof v === 'number' ? String(v) : null),
  },
]

const SERVICE_LINE_SCALAR_CONFIGS: ArrayDiffFieldConfig[] = [
  {
    key: 'service_date_from',
    label: 'Service Date',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  { key: 'cpt_code', label: 'CPT Code', format: formatString },
]

/** Map amount type to display label */
const AMOUNT_TYPE_LABELS: Record<string, string> = {
  billed_amount: 'Billed Amount',
  allowed_amount: 'Allowed Amount',
  discount: 'Discount',
  copayment: 'Copayment',
  coinsurance: 'Co-insurance',
  deductible: 'Deductible',
  adjustments: 'Adjustments',
  non_covered: 'Non-Covered',
  paid: 'Paid Amount',
  other_plan: 'Other Plan',
  // aliases
  billed: 'Billed Amount',
  allowed: 'Allowed Amount',
  co_insurance: 'Co-insurance',
}

// ---------------------------------------------------------------------------
// Payment summary helpers (unchanged)
// ---------------------------------------------------------------------------

function findPreviousPatientClaimsEvent(
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): EobAuditEvent | null {
  for (let i = index - 1; i >= 0; i--) {
    const e = filteredEvents[i]
    if (e.event_type === 'eob_patient_claims_updated') {
      const eob = asEobEvent(e)
      if (eob.patient_claims) return eob
    }
  }
  return null
}

function buildProcessingFields(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): AuditTrailEventDescriptionField[] | null {
  const currentSummary = current.payment_summary
  if (!currentSummary) return null

  const previousSummary = previous?.payment_summary
  const fields: AuditTrailEventDescriptionField[] = []

  for (const config of PAYMENT_SUMMARY_FIELDS) {
    const currentRaw = currentSummary[config.key]
    const currentFormatted = config.format(currentRaw)

    if (!previousSummary) {
      if (currentFormatted !== null) {
        fields.push({
          label: config.label,
          values: [{ diff: [currentFormatted] }],
        })
      }
    } else {
      const prevRaw = previousSummary[config.key]
      const prevFormatted = config.format(prevRaw)

      if (currentFormatted !== prevFormatted) {
        fields.push({
          label: config.label,
          values: [
            {
              previous: prevFormatted ?? '--',
              current: currentFormatted ?? '--',
            },
          ],
        })
      }
    }
  }

  return fields.length > 0 ? fields : null
}

// ---------------------------------------------------------------------------
// Adjustment diff helpers
// ---------------------------------------------------------------------------

function buildAdjustmentDiffs(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): AdjustmentDiffData[] | null {
  const currentAdj = current.payment_summary?.adjustments ?? []
  const previousAdj = previous?.payment_summary?.adjustments ?? []
  if (currentAdj.length === 0 && previousAdj.length === 0) return null

  const isFirst = !previous

  const diffResult = isFirst
    ? computeArrayInitial({
        items: currentAdj as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: ADJUSTMENT_FIELD_CONFIGS,
      })
    : computeArrayDiff({
        currentItems: currentAdj as unknown as Record<string, unknown>[],
        previousItems: previousAdj as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: ADJUSTMENT_FIELD_CONFIGS,
      })

  const results = diffResult.items.filter((item) => item.status !== 'unchanged')
  if (results.length === 0) return null

  return results.map((item) => ({
    adjustmentKey: item.id,
    fields: item.fields,
    status: item.status,
  }))
}

// ---------------------------------------------------------------------------
// Checked patients diff helpers
// ---------------------------------------------------------------------------

function buildCheckedPatientDiffs(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): CheckedPatientDiffData[] | null {
  const currentPatients = current.payment_summary?.checked_patients ?? []
  const previousPatients = previous?.payment_summary?.checked_patients ?? []
  if (currentPatients.length === 0 && previousPatients.length === 0) return null

  const isFirst = !previous

  const diffResult = isFirst
    ? computeArrayInitial({
        items: currentPatients as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: CHECKED_PATIENT_FIELD_CONFIGS,
      })
    : computeArrayDiff({
        currentItems: currentPatients as unknown as Record<string, unknown>[],
        previousItems: previousPatients as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: CHECKED_PATIENT_FIELD_CONFIGS,
      })

  const results = diffResult.items.filter((item) => item.status !== 'unchanged')
  if (results.length === 0) return null

  return results.map((item) => ({
    patientKey: item.id,
    fields: item.fields,
    status: item.status,
  }))
}

// ---------------------------------------------------------------------------
// Patient claims helpers
// ---------------------------------------------------------------------------

/** Flatten amounts array into keyed fields on a flat record for diffing */
function flattenServiceLine(sl: apiClient.ServiceLineResponse): Record<string, unknown> {
  const flat: Record<string, unknown> = {
    service_line_id: sl.service_line_id,
    service_date_from: sl.service_date_from,
    cpt_code: sl.cpt_code,
  }
  if (sl.amounts) {
    for (const amt of sl.amounts) {
      flat[`amount_${amt.type}`] = { amount: amt.amount, amountType: amt.amount_type }
    }
  }
  return flat
}

/** Build ArrayDiffFieldConfigs for amount types present in service lines */
function buildAmountFieldConfigs(
  currentLines: apiClient.ServiceLineResponse[],
  previousLines: apiClient.ServiceLineResponse[],
): ArrayDiffFieldConfig[] {
  const types = new Set<string>()
  for (const sl of [...currentLines, ...previousLines]) {
    if (sl.amounts) {
      for (const amt of sl.amounts) types.add(amt.type)
    }
  }
  const configs: ArrayDiffFieldConfig[] = []
  for (const t of types) {
    const label = AMOUNT_TYPE_LABELS[t] ?? t
    configs.push({ key: `amount_${t}`, label, format: formatAmount })
  }
  return configs
}

function formatCarc(code: string): string {
  const numericPart = code.replace(/^[A-Z]+/, '')
  const entry = carcCodes.find((c) => String(c.code) === numericPart)
  return entry ? `${code} - ${entry.label}` : code
}

function formatRarc(code: string): string {
  const entry = rarcCodes.find((c) => c.code === code)
  return entry ? `${code} - ${entry.label}` : code
}

/** Build code sub-fields for a single code group (CARC N, RARC N, Payor specific code) */
function buildCodeGroupSubFields(
  current: apiClient.ServiceLineCodeInfoOutput | undefined,
  previous: apiClient.ServiceLineCodeInfoOutput | undefined,
  groupNum: string,
  isFirst: boolean,
): AuditTrailEventDescriptionField[] {
  const fields: AuditTrailEventDescriptionField[] = []
  const curCarcs = current?.carcs ?? []
  const curRarcs = current?.rarcs ?? []
  const curPsc = current?.payor_specific_codes ?? []
  const prevCarcs = previous?.carcs ?? []
  const prevRarcs = previous?.rarcs ?? []
  const prevPsc = previous?.payor_specific_codes ?? []

  if (isFirst) {
    if (curCarcs.length > 0)
      fields.push({ label: `CARC${groupNum}`, values: [{ diff: curCarcs.map(formatCarc) }] })
    if (curRarcs.length > 0)
      fields.push({ label: `RARC${groupNum}`, values: [{ diff: curRarcs.map(formatRarc) }] })
    if (curPsc.length > 0)
      fields.push({ label: `Payor specific code${groupNum}`, values: [{ diff: curPsc }] })
  } else {
    const addedCarcs = curCarcs.filter((c) => !prevCarcs.includes(c))
    const removedCarcs = prevCarcs.filter((c) => !curCarcs.includes(c))
    if (addedCarcs.length > 0 || removedCarcs.length > 0) {
      const values: AuditTrailEventDescriptionField['values'] = []
      if (removedCarcs.length > 0)
        values.push({ diff: removedCarcs.map(formatCarc), action: 'removed' })
      if (addedCarcs.length > 0) values.push({ diff: addedCarcs.map(formatCarc), action: 'added' })
      fields.push({ label: `CARC${groupNum}`, values })
    }

    const addedRarcs = curRarcs.filter((c) => !prevRarcs.includes(c))
    const removedRarcs = prevRarcs.filter((c) => !curRarcs.includes(c))
    if (addedRarcs.length > 0 || removedRarcs.length > 0) {
      const values: AuditTrailEventDescriptionField['values'] = []
      if (removedRarcs.length > 0)
        values.push({ diff: removedRarcs.map(formatRarc), action: 'removed' })
      if (addedRarcs.length > 0) values.push({ diff: addedRarcs.map(formatRarc), action: 'added' })
      fields.push({ label: `RARC${groupNum}`, values })
    }

    const addedPsc = curPsc.filter((c) => !prevPsc.includes(c))
    const removedPsc = prevPsc.filter((c) => !curPsc.includes(c))
    if (addedPsc.length > 0 || removedPsc.length > 0) {
      const values: AuditTrailEventDescriptionField['values'] = []
      if (removedPsc.length > 0) values.push({ diff: removedPsc, action: 'removed' })
      if (addedPsc.length > 0) values.push({ diff: addedPsc, action: 'added' })
      fields.push({ label: `Payor specific code${groupNum}`, values })
    }
  }

  return fields
}

/** Extract code sub-fields per amount type for a service line */
function extractCodesPerAmount(
  currentSl: apiClient.ServiceLineResponse | undefined,
  previousSl: apiClient.ServiceLineResponse | undefined,
  isFirst: boolean,
): Map<string, AuditTrailEventDescriptionField[]> {
  const result = new Map<string, AuditTrailEventDescriptionField[]>()
  const currentAmounts = currentSl?.amounts ?? []
  const previousAmounts = previousSl?.amounts ?? []

  const prevCodesMap = new Map<string, apiClient.ServiceLineCodeInfoOutput[]>()
  for (const amt of previousAmounts) {
    if (amt.codes && amt.codes.length > 0) prevCodesMap.set(amt.type, amt.codes)
  }

  const processedTypes = new Set<string>()

  for (const amt of currentAmounts) {
    processedTypes.add(amt.type)
    const currentCodes = amt.codes ?? []
    const prevCodes = prevCodesMap.get(amt.type) ?? []
    if (currentCodes.length === 0 && prevCodes.length === 0) continue

    const hasMultiple = currentCodes.length > 1 || prevCodes.length > 1
    const prevByKey = new Map<string, apiClient.ServiceLineCodeInfoOutput>()
    for (const c of prevCodes) {
      if (c.key) prevByKey.set(c.key, c)
    }

    const subFields: AuditTrailEventDescriptionField[] = []
    const processedKeys = new Set<string>()

    for (let i = 0; i < currentCodes.length; i++) {
      const code = currentCodes[i]
      const key = code.key ?? `${i}`
      processedKeys.add(key)
      const groupNum = hasMultiple ? ` ${i + 1}` : ''
      subFields.push(...buildCodeGroupSubFields(code, prevByKey.get(key), groupNum, isFirst))
    }

    // Removed code groups
    if (!isFirst) {
      for (const prev of prevCodes) {
        if (prev.key && processedKeys.has(prev.key)) continue
        subFields.push(...buildCodeGroupSubFields(undefined, prev, '', false))
      }
    }

    if (subFields.length > 0) result.set(amt.type, subFields)
  }

  // Amounts removed entirely
  if (!isFirst) {
    for (const amt of previousAmounts) {
      if (processedTypes.has(amt.type)) continue
      if (!amt.codes || amt.codes.length === 0) continue
      const fields: AuditTrailEventDescriptionField[] = []
      for (const codeInfo of amt.codes) {
        fields.push(...buildCodeGroupSubFields(undefined, codeInfo, '', false))
      }
      if (fields.length > 0) result.set(amt.type, fields)
    }
  }

  return result
}

/** Merge code sub-fields into their parent amount fields */
function mergeCodesIntoFields(
  fields: AuditTrailEventDescriptionField[],
  codesPerAmount: Map<string, AuditTrailEventDescriptionField[]>,
  labelToAmountType: Map<string, string>,
): AuditTrailEventDescriptionField[] {
  const attachedTypes = new Set<string>()
  const merged: AuditTrailEventDescriptionField[] = []

  for (const field of fields) {
    const amountType = labelToAmountType.get(field.label)
    if (amountType && codesPerAmount.has(amountType)) {
      merged.push({ ...field, subFields: codesPerAmount.get(amountType) })
      attachedTypes.add(amountType)
    } else {
      merged.push(field)
    }
  }

  // Amounts with codes but no value change — add label-only field with subFields
  for (const [amountType, subFields] of codesPerAmount) {
    if (attachedTypes.has(amountType)) continue
    const label = AMOUNT_TYPE_LABELS[amountType] ?? amountType
    merged.push({ label, values: [], subFields })
  }

  return merged
}

/** Build per-service-line diffs for a single claim */
function buildServiceLineDiffs(
  currentClaim: apiClient.PatientClaimResponse | undefined,
  previousClaim: apiClient.PatientClaimResponse | undefined,
  isFirst: boolean,
): ServiceLineDiffData[] {
  const currentLines = currentClaim?.service_lines ?? []
  const previousLines = previousClaim?.service_lines ?? []

  if (currentLines.length === 0 && previousLines.length === 0) return []

  const amountConfigs = buildAmountFieldConfigs(currentLines, previousLines)
  const allFieldConfigs = [...SERVICE_LINE_SCALAR_CONFIGS, ...amountConfigs]

  // Build reverse map: label -> amount type
  const labelToAmountType = new Map<string, string>()
  for (const config of amountConfigs) {
    labelToAmountType.set(config.label, config.key.replace('amount_', ''))
  }

  const flatCurrent = currentLines.map(flattenServiceLine)
  const flatPrevious = previousLines.map(flattenServiceLine)

  const diffResult = isFirst
    ? computeArrayInitial({
        items: flatCurrent,
        idKey: 'service_line_id',
        fieldConfigs: allFieldConfigs,
      })
    : computeArrayDiff({
        currentItems: flatCurrent,
        previousItems: flatPrevious,
        idKey: 'service_line_id',
        fieldConfigs: allFieldConfigs,
      })

  // Build maps from service_line_id to original response for code extraction
  const currentSlMap = new Map<string, apiClient.ServiceLineResponse>()
  for (const sl of currentLines) {
    if (sl.service_line_id) currentSlMap.set(sl.service_line_id, sl)
  }
  const prevSlMap = new Map<string, apiClient.ServiceLineResponse>()
  for (const sl of previousLines) {
    if (sl.service_line_id) prevSlMap.set(sl.service_line_id, sl)
  }

  return diffResult.items
    .filter((item) => {
      if (item.status !== 'unchanged') return true
      // Keep unchanged items if they have code changes
      const codes = extractCodesPerAmount(
        currentSlMap.get(item.id),
        prevSlMap.get(item.id),
        isFirst,
      )
      return codes.size > 0
    })
    .map((item) => {
      const codesPerAmount = extractCodesPerAmount(
        currentSlMap.get(item.id),
        prevSlMap.get(item.id),
        isFirst,
      )
      return {
        serviceLineId: item.id,
        fields: mergeCodesIntoFields(item.fields, codesPerAmount, labelToAmountType),
        status: item.status === 'unchanged' && codesPerAmount.size > 0 ? 'updated' : item.status,
      }
    })
}

/** Build patient claims diff data for the UI */
function buildPatientClaimsDiff(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): PatientClaimDiffData[] | null {
  const currentClaims = current.patient_claims
  if (!currentClaims || currentClaims.length === 0) return null

  const previousClaims = previous?.patient_claims ?? []
  const isFirst = !previous

  // Diff claim-level scalar fields
  const claimDiff = isFirst
    ? computeArrayInitial({
        items: currentClaims as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: CLAIM_FIELD_CONFIGS,
      })
    : computeArrayDiff({
        currentItems: currentClaims as unknown as Record<string, unknown>[],
        previousItems: previousClaims as unknown as Record<string, unknown>[],
        idKey: 'key',
        fieldConfigs: CLAIM_FIELD_CONFIGS,
      })

  // Build map for looking up full claim objects
  const currentMap = new Map<string, apiClient.PatientClaimResponse>()
  for (const c of currentClaims) currentMap.set(c.key, c)
  const prevMap = new Map<string, apiClient.PatientClaimResponse>()
  for (const c of previousClaims) prevMap.set(c.key, c)

  const results: PatientClaimDiffData[] = []

  for (let i = 0; i < claimDiff.items.length; i++) {
    const item = claimDiff.items[i]
    const currentClaim = currentMap.get(item.id)
    const previousClaim = prevMap.get(item.id)

    // Build context line — fall back to previous claim for removed patients
    const claimForName = currentClaim ?? previousClaim
    const firstName = claimForName?.patient_first_name ?? ''
    const lastName = claimForName?.patient_last_name ?? ''
    const name = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown'
    const patientIndex = i + 1
    const contextLine = `${name} (Patient ${patientIndex})`

    const serviceLines = buildServiceLineDiffs(currentClaim, previousClaim, isFirst)

    // Skip claims with no changes (no field diffs and no service line diffs)
    if (item.status === 'unchanged' && serviceLines.length === 0) continue

    results.push({
      claimKey: item.id,
      contextLine,
      claimFields: item.fields,
      serviceLines,
      status: item.status,
    })
  }

  return results.length > 0 ? results : null
}

// ---------------------------------------------------------------------------
// Main transform
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Main transforms
// ---------------------------------------------------------------------------

/**
 * Transform eob_processing_finished into a title-only event.
 */
export function transformEobProcessingFinished(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): EobProcessingFinishedEventProps {
  const hasPrevious = filteredEvents
    .slice(0, index)
    .some((e) => e.event_type === 'eob_processing_finished')

  return {
    type: 'eob-processing-finished',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'eob-processing',
    eventType: event.event_type,
    title: hasPrevious
      ? 'EOB Processing: Document Revalidated'
      : 'EOB Processing: Document Validated',
  }
}

/**
 * Transform eob_payment_summary_updated / eob_patient_claims_updated events.
 * Handles both payment_summary and patient_claims branches.
 */
export function transformEobProcessing(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): EobProcessingEventProps | null {
  const eobEvent = asEobEvent(event)

  // Patient claims branch (eob_patient_claims_updated events)
  if (event.event_type === 'eob_patient_claims_updated') {
    if (!eobEvent.patient_claims) return null
    const previousEvent = findPreviousPatientClaimsEvent(filteredEvents, index)
    const patientClaims = buildPatientClaimsDiff(eobEvent, previousEvent)

    // If a single patient was removed, use a dedicated title
    const removedClaim =
      patientClaims?.length === 1 && patientClaims[0].status === 'removed' ? patientClaims[0] : null
    const title = removedClaim
      ? `EOB Processing: ${removedClaim.contextLine} – Removed`
      : 'EOB Processing: Fields Reviewed'

    return {
      type: 'eob-processing',
      timestamp: event.timestamp,
      user: event.user,
      userRole: event.user ? 'person' : 'opti',
      step: 'eob-processing',
      eventType: event.event_type,
      title,
      fields: null,
      adjustmentDiffs: null,
      checkedPatientDiffs: null,
      patientClaims,
      notes: event.notes ?? null,
    }
  }

  // Payment summary branch (eob_payment_summary_updated events)
  if (event.event_type !== 'eob_payment_summary_updated' || !eobEvent.payment_summary) {
    return null
  }

  const previousEvent = findPreviousPaymentSummaryEvent(filteredEvents, index)
  const fields = buildProcessingFields(eobEvent, previousEvent)
  const adjustmentDiffs = buildAdjustmentDiffs(eobEvent, previousEvent)
  const checkedPatientDiffs = buildCheckedPatientDiffs(eobEvent, previousEvent)

  return {
    type: 'eob-processing',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'eob-processing',
    eventType: event.event_type,
    title: 'EOB Processing: Fields Reviewed',
    fields,
    adjustmentDiffs,
    checkedPatientDiffs,
    patientClaims: null,
    notes: event.notes ?? null,
  }
}

export function getEobProcessingSearchableText(props: EobProcessingEventProps): string {
  const parts = [props.title]
  if (props.fields) parts.push(collectFieldSearchText(props.fields))
  if (props.adjustmentDiffs) {
    for (const adj of props.adjustmentDiffs) {
      if (adj.fields.length > 0) parts.push(collectFieldSearchText(adj.fields))
    }
  }
  if (props.checkedPatientDiffs) {
    for (const cp of props.checkedPatientDiffs) {
      if (cp.fields.length > 0) parts.push(collectFieldSearchText(cp.fields))
    }
  }
  if (props.patientClaims) {
    for (const claim of props.patientClaims) {
      parts.push(claim.contextLine)
      if (claim.claimFields.length > 0) parts.push(collectFieldSearchText(claim.claimFields))
      for (const sl of claim.serviceLines) {
        if (sl.fields.length > 0) parts.push(collectFieldSearchText(sl.fields))
      }
    }
  }
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}

export function getEobProcessingFinishedSearchableText(
  props: EobProcessingFinishedEventProps,
): string {
  return props.title
}
